import { diff, atomizeChangeset } from "json-diff-ts";

/**
 * Get a comma-separated list of updated fields between two JSON objects
 * @param current The current JSON object
 * @param previous The previous JSON object (optional)
 * @returns A comma-separated list of updated field names, or null if no changes
 */
export function getJsonDiffSummary(
  current: unknown,
  previous: unknown | null | undefined,
): string | null {
  // Handle undefined/null values
  if (current === undefined || current === null) {
    if (previous === undefined || previous === null) {
      return null; // Both are undefined/null, no change
    }
    return "All fields removed";
  }

  if (previous === undefined || previous === null) {
    return "Initial snapshot";
  }

  try {
    const differences = diff(previous, current);

    if (!differences || differences.length === 0) {
      return null;
    }

    // Atomize changeset to get paths for nested changes
    const atomicChanges = atomizeChangeset(differences);

    const updatedFields: string[] = [];
    const excludeKeys = ["modified_at"];

    for (const change of atomicChanges) {
      let path = change.path;
      // Remove leading "$." prefix if present
      if (path.startsWith("$.")) {
        path = path.substring(2);
      }
      // Skip excluded keys
      if (excludeKeys.some((key) => path === key || path.endsWith(`.${key}`))) {
        continue;
      }
      // Include all types of changes (ADD, REMOVE, UPDATE) as "updated fields"
      updatedFields.push(path);
    }

    // Remove duplicates and sort
    const uniqueFields = Array.from(new Set(updatedFields)).sort();

    return uniqueFields.length > 0 ? uniqueFields.join(", ") : null;
  } catch (error) {
    console.error("Error computing JSON diff:", error);
    return "Error computing diff";
  }
}
